import { z } from 'zod'

/**
 * Section that renders an editable table backed by a single array field.
 * Columns are derived from the ontology field's items.properties,
 * ordered by each property's sortOrder.
 */
export const ArrayTableSectionSchema = z.object({
  id: z.string(),
  type: z.literal('array_table'),
  title: z.string().optional(),
  /** The ontology field ID that stores the array value */
  field: z.string(),
  /**
   * Optional default sort applied to the table on first render, before the user
   * clicks any column header. `field` is a column key (an items.properties key).
   * When omitted, the table falls back to sorting by the first date column
   * descending (newest first), if one exists.
   */
  defaultSort: z
    .object({
      field: z.string(),
      order: z.enum(['ascending', 'descending']),
    })
    .optional(),
  /**
   * When present, the agent reads this list to know which fields to populate.
   * For array_table sections, this is typically ["<field>"] (the backing field itself).
   */
  fields: z.array(z.string()).optional(),
  /**
   * Optional agent config block. When present, an AI autofill button is rendered
   * for this section. The content is opaque to Forecast — it is read by the
   * gong-agent-service via the workflow config.
   */
  agent: z.record(z.string(), z.unknown()).optional(),
  /**
   * When present, the AI autofill banner is only shown to users
   * that hold at least one of these permissions, along with Gecko Internal (by default)
   */
  agentPermissions: z.array(z.string()).optional(),
})

export type ArrayTableSection = z.infer<typeof ArrayTableSectionSchema>
